import { PartnersCapitalCategory, Schema } from 'further-types/lp/account';
import { AssetType } from 'further-types/lp/asset';

export const AssetTypeLabels: Record<AssetType, string> = {
  [AssetType.Company]: 'Company',
};

export const PayableReceivableCategoryLabels: Record<
  Schema.PayableReceivableCategory,
  string
> = {
  [Schema.PayableReceivableCategory.ReceivableOperations]:
    'Receivables (operations)',
  [Schema.PayableReceivableCategory.ReceivableInvestment]:
    'Receivables (investment)',
  [Schema.PayableReceivableCategory.ReceivableCapital]: 'Receivables (capital)',
  [Schema.PayableReceivableCategory.ReceivableBankTransfer]:
    'Receivables (bank transfer)',
  [Schema.PayableReceivableCategory.PrepaidExpenses]: 'Prepaid expenses',
  [Schema.PayableReceivableCategory.PayableOperations]: 'Payables (operations)',
  [Schema.PayableReceivableCategory.PayableInvestment]: 'Payables (investment)',
  [Schema.PayableReceivableCategory.PayableCapital]: 'Payables (capital)',
  [Schema.PayableReceivableCategory.DeferredIncome]: 'Deferred income',
  [Schema.PayableReceivableCategory.AccruedExpensesOperations]:
    'Accrued expenses (operations)',
  [Schema.PayableReceivableCategory.AccruedIncomeOperations]:
    'Accrued income (operations)',
};

export function getPayableReceivableCategoryLabel(
  raw: string | null | undefined,
): string {
  if (!raw) {
    return '';
  }
  const trimmed = raw.trim();
  for (const [enumKey, label] of Object.entries(
    PayableReceivableCategoryLabels,
  )) {
    if (enumKey === trimmed) {
      return label;
    }
  }
  return trimmed;
}

export function parsePayableReceivableCategory(
  value: string | undefined | null,
): Schema.PayableReceivableCategory | null {
  if (!value) return null;
  const normalized = value.toLowerCase().trim();
  const entry = Object.entries(PayableReceivableCategoryLabels).find(
    ([, label]) => label.toLowerCase() === normalized,
  );
  return entry ? (entry[0] as Schema.PayableReceivableCategory) : null;
}

export const AccountSectionLabels = {
  [Schema.AccountSection.Assets]: 'Assets',
  [Schema.AccountSection.Expenses]: 'Expenses',
  [Schema.AccountSection.Income]: 'Income',
  [Schema.AccountSection.Payables]: 'Payables',
  [Schema.AccountSection.PartnersCapital]: "Partners' Capital",
};

export function parseAccountSection(
  value: string | undefined | null,
): Schema.AccountSection | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase().trim();
  for (const [key, label] of Object.entries(AccountSectionLabels)) {
    if (label.toLowerCase() === normalized) {
      return key as Schema.AccountSection;
    }
  }
  return undefined;
}

export const PartnersCapitalCategoryLabels: Record<
  PartnersCapitalCategory,
  string
> = {
  [PartnersCapitalCategory.ContributionsInsideCommitment]:
    'Contributions - Inside commitment',
  [PartnersCapitalCategory.ContributionsOutsideOfCommitment]:
    'Contributions - Outside commitment',
  [PartnersCapitalCategory.RecallableDistributions]: 'Recallable distributions',
  [PartnersCapitalCategory.NonRecallableDistributions]:
    'Non-recallable distributions',
};

export const BalanceSheetSectionLabels = {
  [Schema.BalanceSheetSection.CashAndCashEquivalents]:
    'Cash and cash equivalents',
  [Schema.BalanceSheetSection.InvestmentsAtCost]: 'Investments at cost',
  [Schema.BalanceSheetSection.UnrealisedGainLoss]:
    'Unrealised gain/loss on investments',
  [Schema.BalanceSheetSection.UnrealisedFXGainLoss]:
    'Unrealised FX gain/loss on investments',
  [Schema.BalanceSheetSection.ReceivablesAndPrepayments]:
    'Receivables and prepayments',
  [Schema.BalanceSheetSection.AccruedIncome]: 'Accrued income',
  [Schema.BalanceSheetSection.CapitalDueFromPartners]:
    'Capital due from partners',
  [Schema.BalanceSheetSection.AccountsPayable]: 'Accounts payable',
  [Schema.BalanceSheetSection.AccruedExpenses]: 'Accrued expenses',
  [Schema.BalanceSheetSection.CapitalDueToPartners]: 'Capital due to partners',
};

export const BalanceSheetSectionsByAccountSection: Record<
  Schema.AccountSection,
  Array<Schema.BalanceSheetSection>
> = {
  [Schema.AccountSection.Assets]: [
    Schema.BalanceSheetSection.CashAndCashEquivalents,
    Schema.BalanceSheetSection.InvestmentsAtCost,
    Schema.BalanceSheetSection.UnrealisedGainLoss,
    Schema.BalanceSheetSection.UnrealisedFXGainLoss,
    Schema.BalanceSheetSection.ReceivablesAndPrepayments,
    Schema.BalanceSheetSection.AccruedIncome,
    Schema.BalanceSheetSection.CapitalDueFromPartners,
  ],
  [Schema.AccountSection.Payables]: [
    Schema.BalanceSheetSection.AccountsPayable,
    Schema.BalanceSheetSection.AccruedExpenses,
    Schema.BalanceSheetSection.CapitalDueToPartners,
  ],
  [Schema.AccountSection.PartnersCapital]: [],
  [Schema.AccountSection.Income]: [],
  [Schema.AccountSection.Expenses]: [],
};

export function parseBalanceSheetSection(
  value: string | undefined | null,
): Schema.BalanceSheetSection | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase().trim();
  for (const [key, label] of Object.entries(BalanceSheetSectionLabels)) {
    if (label.toLowerCase() === normalized) {
      return key as Schema.BalanceSheetSection;
    }
  }
  return undefined;
}

export function parsePartnersCapitalCategory(
  value: string | undefined | null,
): PartnersCapitalCategory | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase().trim();
  for (const [key, label] of Object.entries(PartnersCapitalCategoryLabels)) {
    if (label.toLowerCase() === normalized) {
      return key as PartnersCapitalCategory;
    }
  }
  return undefined;
}
